import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Ruler, Plus, Search, Edit, Trash2, Upload, FileSpreadsheet, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalibresPage = () => {
  const { currentOrg, user, isSuperAdmin } = useAuth();
  const [calibres, setCalibres] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', description: '' });
  const [saving, setSaving] = useState(false);

  const [deleteTarget, setDeleteTarget] = useState(null);

  const [seedDialogOpen, setSeedDialogOpen] = useState(false);
  const [seedFile, setSeedFile] = useState(null);
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState(null);
  const seedInputRef = useRef(null);

  const canManage = isSuperAdmin?.() || ['admin', 'accountant'].includes(user?.role);
  const canDelete = isSuperAdmin?.() || user?.role === 'admin';

  useEffect(() => {
    if (currentOrg) fetchCalibres();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg]);

  const fetchCalibres = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/calibres`, { params: { organization_id: currentOrg.id } });
      setCalibres(res.data || []);
    } catch (error) {
      toast.error('Failed to load calibres');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', description: '' });
    setDialogOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name || '', description: c.description || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/calibres/${editing.id}`, {
          name: form.name.trim(),
          description: form.description || null,
        });
        toast.success('Calibre updated');
      } else {
        await axios.post(`${API}/calibres`, {
          name: form.name.trim(),
          description: form.description || null,
          organization_id: currentOrg.id,
        });
        toast.success('Calibre created');
      }
      setDialogOpen(false);
      fetchCalibres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save calibre');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/calibres/${deleteTarget.id}`);
      toast.success('Calibre deleted');
      setDeleteTarget(null);
      fetchCalibres();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete calibre');
    }
  };

  const handleSeed = async () => {
    if (!seedFile) return;
    setSeeding(true);
    setSeedResult(null);
    try {
      const payload = new FormData();
      payload.append('file', seedFile);
      payload.append('organization_id', currentOrg.id);
      const res = await axios.post(`${API}/calibres/seed`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSeedResult(res.data);
      toast.success(`Seeded ${res.data.created} calibre(s)`);
      fetchCalibres();
    } catch (error) {
      setSeedResult({ error: error.response?.data?.detail || 'Failed to seed calibres' });
    } finally {
      setSeeding(false);
    }
  };

  const filtered = calibres.filter((c) =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 lg:space-y-6" data-testid="calibres-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-md bg-primary/15 text-primary">
            <Ruler className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-xl lg:text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Calibres
            </h1>
            <p className="text-sm text-muted-foreground">Manage fish sizes / grades</p>
          </div>
        </div>

        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => { setSeedDialogOpen(true); setSeedFile(null); setSeedResult(null); }} data-testid="seed-calibres-btn">
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              Seed from File
            </Button>
            <Button className="btn-glow" onClick={openAdd} data-testid="add-calibre-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Calibre
            </Button>
          </div>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-sm" data-testid="calibre-search">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search calibres..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
        />
      </div>

      {/* List */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 flex items-center justify-center text-muted-foreground">
              <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center" data-testid="calibres-empty">
              <Ruler className="w-10 h-10 mx-auto text-muted-foreground/50 mb-3" />
              <p className="font-medium">No calibres yet</p>
              <p className="text-sm text-muted-foreground mb-4">
                Add calibres manually or seed them from your Excel file.
              </p>
              {canManage && (
                <Button variant="outline" onClick={openAdd}>
                  <Plus className="w-4 h-4 mr-2" /> Add your first calibre
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th className="w-16">#</th>
                    <th>Name</th>
                    <th>Description</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c, idx) => (
                    <tr key={c.id} data-testid={`calibre-row-${c.id}`}>
                      <td className="text-muted-foreground font-mono text-sm">{idx + 1}</td>
                      <td className="font-medium">{c.name}</td>
                      <td className="text-muted-foreground text-sm">{c.description || '-'}</td>
                      <td>
                        <div className="flex gap-1 justify-end">
                          {canManage && (
                            <Button variant="ghost" size="sm" onClick={() => openEdit(c)} title="Edit" data-testid={`edit-calibre-${c.id}`}>
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button variant="ghost" size="sm" className="text-red-400" onClick={() => setDeleteTarget(c)} title="Delete" data-testid={`delete-calibre-${c.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Calibre' : 'Add Calibre'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update the calibre details.' : 'Create a new fish size / grade.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                placeholder="e.g., G, M, PP, 0/100..."
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                autoFocus
                data-testid="calibre-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Optional description..."
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                data-testid="calibre-description-input"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="save-calibre-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editing ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Seed Dialog */}
      <Dialog open={seedDialogOpen} onOpenChange={(open) => { setSeedDialogOpen(open); if (!open) { setSeedFile(null); setSeedResult(null); } }}>
        <DialogContent className="max-w-[95vw] sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-primary" />
              Seed Calibres from Excel
            </DialogTitle>
            <DialogDescription>
              Upload an Excel file with columns <span className="font-mono">CalibreID</span> and <span className="font-mono">Calibre</span>.
              Existing calibres are skipped (safe to re-run).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <input
              ref={seedInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => { setSeedFile(e.target.files?.[0] || null); setSeedResult(null); }}
              data-testid="calibre-seed-file-input"
            />
            <Button variant="outline" className="w-full" onClick={() => seedInputRef.current?.click()} data-testid="calibre-seed-select-btn">
              <Upload className="w-4 h-4 mr-2" />
              {seedFile ? seedFile.name : 'Select Excel File'}
            </Button>
            {seedResult && !seedResult.error && (
              <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm" data-testid="calibre-seed-result">
                <p className="font-medium text-emerald-500">{seedResult.message}</p>
                <p className="text-muted-foreground mt-1">Created: {seedResult.created} · Skipped: {seedResult.skipped}</p>
              </div>
            )}
            {seedResult?.error && (
              <div className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-500">{seedResult.error}</div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setSeedDialogOpen(false)}>Close</Button>
            <Button onClick={handleSeed} disabled={!seedFile || seeding} data-testid="calibre-seed-btn">
              {seeding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 mr-2" />}
              Seed
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete calibre?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete "{deleteTarget?.name}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-calibre-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CalibresPage;
